import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import { FaLock, FaCreditCard, FaCheck, FaGlobe, FaServer } from 'react-icons/fa';
import { orderService } from '../../services/orderService';
import { stripePromise } from '../../config/stripe';
import StripePaymentForm from '../../components/checkout/StripePaymentForm';

interface CheckoutData {
  domain: {
    name: string;
    price: number;
    type: string;
  };
  hosting: {
    plan: any;
    planId: string;
  } | null;
  contactInfo: any;
  authCode: string;
  total: number;
}

const DomainTransferCheckout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [orderCreated, setOrderCreated] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Scroll to top of page when component mounts
    window.scrollTo(0, 0);

    // Check if user is authenticated
    if (!user && !localStorage.getItem('token')) {
      navigate('/login?message=Please sign in to complete checkout');
      return;
    }

    // Get checkout data from navigation state
    if (location.state?.checkoutData) {
      setCheckoutData(location.state.checkoutData);
    } else {
      // Redirect back to domain transfer if no data
      navigate('/domain-transfer');
    }
  }, [user, location.state, navigate]);

  /**
   * F5: replace the previous fake setTimeout with a real order + Stripe flow.
   *
   * 1. Create an order on the backend with domain_action='transfer' (so it
   *    is recorded and auditable), payment_status=pending.
   * 2. The StripePaymentForm rendered below charges the card via real Stripe.
   * 3. Stripe webhook marks the order paid. We do NOT auto-call AWS
   *    TransferDomain here — transfers require the customer's authCode and
   *    a manual coordination step with their previous registrar. The order
   *    sits in 'paid' status; admin sees it in the orders list and runs the
   *    transfer manually through Route 53.
   *
   * The confirmation page says exactly that — no false "your domain is
   * transferred!" claim.
   */
  const createTransferOrder = async () => {
    if (orderCreated) return orderCreated;
    if (!checkoutData || !user) return null;

    if (!localStorage.getItem('token')) {
      setError('You must be signed in to complete checkout. Please log in and try again.');
      return null;
    }

    setError('');
    const orderData = {
      packageId: checkoutData.hosting?.planId || '',
      domainName: checkoutData.domain.name,
      domainAction: 'transfer' as const,
      billingCycle: 'yearly' as const,
      contactInfo: checkoutData.contactInfo,
      pricingDetails: {
        hostingPrice: checkoutData.hosting?.plan?.price || 0,
        domainPrice: checkoutData.domain.price,
        discount: 0,
        total: checkoutData.total,
      },
      paymentMethod: 'stripe' as const,
      serviceType: 'domain-transfer',
      total_amount: checkoutData.total,
      currency: 'USD',
      status: 'pending',
      payment_status: 'pending',
      billing_address: checkoutData.contactInfo?.address || {},
      items: [
        {
          type: 'domain',
          action: 'transfer',
          domainName: checkoutData.domain.name,
          authCode: checkoutData.authCode,
          price: checkoutData.domain.price,
        },
        ...(checkoutData.hosting?.plan
          ? [{ type: 'hosting', packageId: checkoutData.hosting.planId, billingCycle: 'yearly', price: checkoutData.hosting.plan.price }]
          : []),
      ],
    };

    const result = await orderService.createOrder(orderData);
    if (!result.success || !result.data) {
      setError(result.error || 'Failed to create transfer order. Please try again.');
      return null;
    }
    setOrderCreated(result.data);
    return result.data;
  };

  const handleContinueToPayment = async () => {
    if (paymentMethod !== 'card') {
      setError('Only card payments are supported at the moment.');
      return;
    }
    setProcessing(true);
    try {
      await createTransferOrder();
    } catch (e: any) {
      setError(e.message || 'Failed to create order');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = (paymentIntentId: string) => {
    // Transfer is intentionally NOT auto-completed at AWS — see comment on
    // createTransferOrder above. Customer is redirected to a "transfer
    // requested" page; admin handles the actual TransferDomain call.
    navigate('/checkout/success', {
      state: {
        type: 'domain-transfer',
        domain: checkoutData?.domain.name,
        total: checkoutData?.total,
        orderId: orderCreated?.id,
        orderNumber: orderCreated?.order_number,
        paymentIntentId,
        message: 'Transfer requested — our team will contact you within 1 business day to coordinate with your previous registrar.',
      },
    });
  };

  const handlePaymentError = (msg: string) => {
    setError(msg);
    setProcessing(false);
  };

  if (!checkoutData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h1>
            <p className="text-gray-600">Please wait while we load your checkout information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Order Summary */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
              
              {/* Domain Transfer */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mr-4">
                      <FaGlobe className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{checkoutData.domain.name}</h3>
                      <p className="text-sm text-gray-600">Domain Transfer + 1 Year Extension</p>
                      <div className="flex items-center mt-2 text-xs text-green-600">
                        <FaCheck className="w-3 h-3 mr-1" />
                        <span>Free WHOIS Privacy</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">${checkoutData.domain.price.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Hosting (if selected) */}
              {checkoutData.hosting && (
                <div className="border-b border-gray-200 pb-6 mb-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 rounded-xl flex items-center justify-center mr-4">
                        <FaServer className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{checkoutData.hosting.plan.name}</h3>
                        <p className="text-sm text-gray-600">First Month</p>
                        <div className="flex items-center mt-2 text-xs text-green-600">
                          <FaCheck className="w-3 h-3 mr-1" />
                          <span>Free SSL Certificate</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">${parseFloat(checkoutData.hosting.plan.price_monthly || checkoutData.hosting.plan.price || '0').toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold text-gray-900">Total</span>
                <span className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                  ${checkoutData.total.toFixed(2)}
                </span>
              </div>

              {/* What's Included */}
              <div className="mt-8 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-4">What's Included:</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                    <span className="text-gray-700">Free domain transfer</span>
                  </div>
                  <div className="flex items-center">
                    <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                    <span className="text-gray-700">1-year domain extension</span>
                  </div>
                  <div className="flex items-center">
                    <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                    <span className="text-gray-700">Free WHOIS privacy protection</span>
                  </div>
                  <div className="flex items-center">
                    <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                    <span className="text-gray-700">Zero downtime transfer</span>
                  </div>
                  {checkoutData.hosting && (
                    <>
                      <div className="flex items-center">
                        <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                        <span className="text-gray-700">Free SSL certificate</span>
                      </div>
                      <div className="flex items-center">
                        <FaCheck className="w-4 h-4 text-green-500 mr-3" />
                        <span className="text-gray-700">24/7 customer support</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <div className="flex items-center mb-6">
                <FaLock className="w-5 h-5 text-green-500 mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">Secure Payment</h2>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h3>
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="card"
                      checked={paymentMethod === 'card'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <FaCreditCard className="w-5 h-5 text-gray-600 mr-2" />
                    <span className="text-gray-700">Credit/Debit Card</span>
                  </label>
                </div>
              </div>

              {/* F5: Real Stripe payment. The fake card form below was
                  replaced because it didn't actually charge anything. Two
                  stages: create the order, then render Stripe Elements. */}
              {error && (
                <div className="mt-4 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              {!orderCreated ? (
                <button
                  onClick={handleContinueToPayment}
                  disabled={processing}
                  className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed mt-8 flex items-center justify-center"
                >
                  <FaLock className="mr-3" />
                  {processing ? 'Creating order…' : `Continue to Payment — $${checkoutData.total.toFixed(2)}`}
                </button>
              ) : (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Payment Details</h4>
                  <Elements stripe={stripePromise}>
                    <StripePaymentForm
                      amount={checkoutData.total}
                      orderId={orderCreated.id}
                      customerEmail={checkoutData.contactInfo?.email || user?.email || ''}
                      customerName={checkoutData.contactInfo?.firstName ? `${checkoutData.contactInfo.firstName} ${checkoutData.contactInfo.lastName || ''}` : (user?.firstName || '')}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      isProcessing={processing}
                      setIsProcessing={setProcessing}
                    />
                  </Elements>
                </div>
              )}

              <p className="text-center text-sm text-gray-600 mt-4">
                Your payment is secured with 256-bit SSL encryption
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainTransferCheckout;